import React, { useState, useEffect } from 'react';
import { MessageCircle, AlertTriangle, CheckCircle, Clock, User, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function SupportDisputes() {
  const [tickets, setTickets] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('tickets');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      
      // Fetch support tickets
      const ticketsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/support/tickets`, {
        headers: { }
      });
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setTickets(data.tickets || []);
      }

      // Fetch disputes
      const disputesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/disputes`, {
        headers: { }
      });
      if (disputesRes.ok) {
        const data = await disputesRes.json();
        setDisputes(data.disputes || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleResolveTicket = async (ticketId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/support/tickets/${ticketId}/resolve`,
        {
          method: 'PATCH',
          headers: { }
        }
      );

      if (response.ok) {
        toast.success('Ticket resolved successfully');
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to resolve ticket');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return styles[status] || styles.open;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return styles[priority] || styles.medium;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <p className="text-gray-600">Loading support data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-disputes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Support & Disputes</h1>
          <p className="text-gray-600 mt-1">Manage support tickets and payment disputes</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open Tickets</p>
                <p className="text-2xl font-bold text-gray-900">
                  {tickets.filter(t => t.status === 'open').length}
                </p>
              </div>
              <MessageCircle className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">
                  {tickets.filter(t => t.status === 'in_progress').length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open Disputes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {disputes.filter(d => d.status === 'open').length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Resolved</p>
                <p className="text-2xl font-bold text-gray-900">
                  {tickets.filter(t => t.status === 'resolved').length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          onClick={() => setActiveView('tickets')}
          className={activeView === 'tickets' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}
        >
          Support Tickets ({tickets.length})
        </Button>
        <Button
          onClick={() => setActiveView('disputes')}
          className={activeView === 'disputes' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}
        >
          Payment Disputes ({disputes.length})
        </Button>
      </div>

      {/* Support Tickets */}
      {activeView === 'tickets' && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Support Tickets</h3>
            <div className="space-y-4">
              {tickets.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No support tickets</p>
                </div>
              ) : (
                tickets.map((ticket) => (
                  <div
                    key={ticket._id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">#{ticket.ticket_id || ticket._id.slice(-6)}</h4>
                          <Badge className={getStatusBadge(ticket.status)}>
                            {ticket.status || 'open'}
                          </Badge>
                          <Badge className={getPriorityBadge(ticket.priority)}>
                            {ticket.priority || 'medium'}
                          </Badge>
                        </div>
                        <p className="text-gray-900 font-medium mb-1">{ticket.subject || 'Support Request'}</p>
                        <p className="text-sm text-gray-600 mb-3">{ticket.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {ticket.user_name || ticket.user_email}
                          </span>
                          <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleResolveTicket(ticket._id)}
                        >
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Disputes */}
      {activeView === 'disputes' && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Disputes</h3>
            <div className="space-y-4">
              {disputes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No payment disputes</p>
                </div>
              ) : (
                disputes.map((dispute) => (
                  <div
                    key={dispute._id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-gray-900">Dispute #{dispute._id.slice(-6)}</h4>
                          <Badge className={getStatusBadge(dispute.status)}>
                            {dispute.status || 'open'}
                          </Badge>
                        </div>
                        <p className="text-gray-900 font-medium mb-1">{dispute.reason || 'Payment Dispute'}</p>
                        <p className="text-sm text-gray-600 mb-3">{dispute.description}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {dispute.patient_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            ${dispute.amount?.toLocaleString()}
                          </span>
                          <span>{new Date(dispute.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      {dispute.status === 'open' && (
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Review
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
